import * as React from "react";
import { useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as KeyRequestsState from "../../store/KeyRequests";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { confirmPrompt, defaultPageSize, formatLocalTime } from "../../helpers";
import { KeyRequestResponse, KeyRequestStatus } from "../../api/ApiClient";
import { Spinner } from "../shared/Spinner";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";

type OwnProps = {};

type Props = KeyRequestsState.KeyRequestsState &
  typeof KeyRequestsState.actionCreators &
  OwnProps;

const statusEnumToStr = (status?: KeyRequestStatus) => {
  switch (status) {
    case KeyRequestStatus.Initial:
      return "Initial";
    case KeyRequestStatus.Approved:
      return "Approved, credential not yet processed";
    case KeyRequestStatus.KeyIssueRequested:
      return "Processing";
    case KeyRequestStatus.KeyIssueSucceeded:
      return "Credential Issued/Door Opened";
    case KeyRequestStatus.KeyIssueFailed:
      return "Credential Issue Failed/Door Open Failed";
    default:
      return "Unknown status";
  }
};
const statusEnumToColorClass = (status?: KeyRequestStatus) => {
  switch (status) {
    case KeyRequestStatus.Approved:
      return "bg-amber-50 text-amber-700 ring-amber-600/20";
    case KeyRequestStatus.KeyIssueRequested:
      return "bg-red-50 text-red-700 ring-red-600/20";
    case KeyRequestStatus.KeyIssueSucceeded:
      return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
    case KeyRequestStatus.KeyIssueFailed:
      return "bg-red-50 text-red-700 ring-red-600/20";
    default:
      return "bg-slate-100 text-slate-600 ring-slate-500/20";
  }
};

const formatKeyRequestDate = (date?: Date | string) =>
  date ? new Date(date).toLocaleString() : "";

const formatKeyRequestLocalTime = (date?: Date | string) => {
  if (!date) {
    return "";
  }

  return formatLocalTime(date instanceof Date ? date.toISOString() : date);
};

const getLockNames = (keyRequest: KeyRequestResponse) =>
  (keyRequest.lockIds ?? [])
    .map((lockId) => lockId.lock?.name ?? "")
    .filter(Boolean)
    .join(", ");

const getSystemLockIds = (keyRequest: KeyRequestResponse) =>
  (keyRequest.lockIds ?? [])
    .map((lockId) => lockId.systemLockId ?? "")
    .filter(Boolean)
    .join(", ");

export const KeyRequests: React.FC<Props> = (props) => {
  const { requestKeyRequests } = props;
  const { enqueueSnackbar } = useSnackbar();

  const deleteKeyRequest = useCallback(
    (id: string) => {
      confirmPrompt({
        promptMessage: "Are you sure you want to delete this key request?",
        onConfirm: async () => {
          try {
            await props.deleteKeyRequest({ id });
            enqueueSnackbar("Key request deleted", { variant: "success" });
          } catch (e) {
            enqueueSnackbar(
              "Error deleting key request: " +
                (e instanceof Error ? e.message : JSON.stringify(e)),
              { variant: "error" },
            );
          }
        },
      });
    },
    [enqueueSnackbar, props],
  );

  const refresh = useCallback(async () => {
    try {
      await requestKeyRequests();
    } catch (e) {
      enqueueSnackbar(
        "Error requesting key requests: " +
          (e instanceof Error ? e.message : JSON.stringify(e)),
        { variant: "error" },
      );
    }
  }, [requestKeyRequests, enqueueSnackbar]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const rows = props.keyRequests;

  return (
    <main className="space-y-6">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7 text-slate-900">
            All Key Requests
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            All key requests registered in the system.
          </p>
        </div>
        <Button
          className="mt-4 sm:mt-0"
          disabled={props.isLoading}
          onClick={refresh}
          type="button"
        >
          {props.isLoading ? "Loading..." : "Refresh"}
        </Button>
      </header>

      <AdminTable
        aria-labelledby="key-requests-heading"
        className="min-w-[2200px]"
        footer={
          <AdminTablePagination
            currentPage={props.page}
            pageSize={props.pageSize || defaultPageSize}
            visibleRowCount={rows.length}
            isLoading={props.isLoading}
            disablePrevious={props.page === 0}
            disableNext={props.keyRequests.length < props.pageSize}
            onNext={props.nextKeyRequestPage}
            onPrevious={props.prevKeyRequestPage}
            onPageSizeChange={(size) =>
              props.setFetchParamsKeyRequests({
                page: 0,
                pageSize: size,
                searchTerm: props.searchTerm,
              })
            }
          />
        }
        toolbar={
          <div className="px-4 py-4">
            <h2 id="key-requests-heading" className="text-base font-semibold text-slate-900">
              Key Requests
            </h2>
          </div>
        }
      >
        <thead>
          <tr>
            <th scope="col" className="min-w-62.5">ID</th>
            <th scope="col" className="min-w-35">Guest</th>
            <th scope="col" className="min-w-42.5">Created</th>
            <th scope="col" className="min-w-42.5">Updated</th>
            <th scope="col" className="min-w-55">Status</th>
            <th scope="col" className="min-w-55">Error</th>
            <th scope="col" className="min-w-35">System Type</th>
            <th scope="col" className="min-w-37.5">Credential Type</th>
            <th scope="col" className="min-w-40">Credential ID</th>
            <th scope="col" className="min-w-40">Note</th>
            <th scope="col" className="min-w-45">Valid From</th>
            <th scope="col" className="min-w-45">Valid To</th>
            <th scope="col" className="min-w-55">Details</th>
            <th scope="col" className="min-w-40">Site</th>
            <th scope="col" className="min-w-50">System Site ID</th>
            <th scope="col" className="min-w-45">Locks</th>
            <th scope="col" className="min-w-52.5">System Lock IDs</th>
            <th scope="col" className="min-w-37.5">User</th>
            <th scope="col" className="min-w-45">System User ID</th>
          </tr>
        </thead>
        <tbody>
          {props.isLoading ? (
            <tr>
              <td colSpan={19} className="py-12 text-center">
                <div className="flex items-center justify-center gap-3 text-sm text-slate-500">
                  <Spinner />
                  Loading key requests...
                </div>
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={19} className="py-16 text-sm text-center text-slate-500">
                No key requests found.
              </td>
            </tr>
          ) : (
            rows.map((keyRequest, index) => (
              <tr key={keyRequest.id ?? `key-request-${index}`}>
                <td>
                  <div className="flex items-start justify-between min-w-0 gap-2">
                    <span className="min-w-0 font-mono text-xs wrap-break-word text-slate-700">
                      {keyRequest.id ?? "--"}
                    </span>
                    <Button
                      aria-label={`Delete key request ${keyRequest.id ?? ""}`}
                      className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => deleteKeyRequest(keyRequest.id ?? "0")}
                      size="icon-xs"
                      type="button"
                      variant="ghost"
                    >
                      <FontAwesomeIcon aria-hidden="true" icon={faTrash} />
                    </Button>
                  </div>
                </td>
                <td>{keyRequest.guestInvite?.guestId ?? "--"}</td>
                <td className="whitespace-nowrap">
                  {formatKeyRequestDate(keyRequest.createdTimestamp) || "--"}
                </td>
                <td className="whitespace-nowrap">
                  {formatKeyRequestDate(keyRequest.updatedTimestamp) || "--"}
                </td>
                <td>
                  <span
                    className={`inline-flex max-w-55 rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${statusEnumToColorClass(keyRequest.status)}`}
                  >
                    {statusEnumToStr(keyRequest.status)}
                  </span>
                </td>
                <td className="max-w-70 wrap-break-word text-rose-700">
                  {keyRequest.error ?? "--"}
                </td>
                <td>{keyRequest.site?.account?.systemType ?? "--"}</td>
                <td>{keyRequest.credentialType ?? "--"}</td>
                <td className="font-mono text-xs break-all">
                  {keyRequest.credentialId ?? "--"}
                </td>
                <td className="max-w-55 wrap-break-word">{keyRequest.note ?? "--"}</td>
                <td className="whitespace-nowrap">
                  {formatKeyRequestLocalTime(keyRequest.validFrom) || "--"}
                </td>
                <td className="whitespace-nowrap">
                  {formatKeyRequestLocalTime(keyRequest.validTo) || "--"}
                </td>
                <td className="max-w-70 wrap-break-word">{keyRequest.details ?? "--"}</td>
                <td>{keyRequest.site?.name ?? "--"}</td>
                <td className="font-mono text-xs break-all">
                  {keyRequest.site?.systemSiteId ?? "--"}
                </td>
                <td
                  className="max-w-60"
                  style={{
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  <div className="leading-5 max-w-60 wrap-break-word">
                    {getLockNames(keyRequest) || "--"}
                  </div>
                </td>
                <td>
                  <div className="font-mono text-xs leading-5 break-all max-w-65">
                    {getSystemLockIds(keyRequest) || "--"}
                  </div>
                </td>
                <td>{keyRequest.connectUser?.name ?? "--"}</td>
                <td className="font-mono text-xs break-all">
                  {keyRequest.siteUser?.systemUserId ?? "--"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </AdminTable>
    </main>
  );
};

export default connect<any, any, any, ApplicationState>(
  (state) => state.keyRequests,
  KeyRequestsState.actionCreators,
)(KeyRequests);
